/**
 * Local-first hooks for LIVE CLASSES (F3-G). The session and the roster
 * arrive via the per-role streams (my_cohorts_student/teacher): the student
 * sees their session; the teacher also sees the cohort's roster. Actual
 * access to the video call is authorized by the server (`/api/live/.../join`).
 */
import type { Accessor } from "solid-js";
import {
  cohortQueryKeys,
  getCohortQuery,
  getSessionByIdQuery,
  scheduleQueryKeys,
} from "@space/shared";
import { createPowerSyncQuery } from "@space/shared/solid";
import type { ClassSession, Cohort } from "@space/powersync";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { OFFLINE_CACHE_STALE_TIME_MS } from "@/lib/constants";

function useBase() {
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  return { db, drizzle, staleTime: OFFLINE_CACHE_STALE_TIME_MS };
}

/** A class session by id (synced columns). */
export function useLiveSession(sessionId: Accessor<string | undefined>) {
  const base = useBase();
  return createPowerSyncQuery<ClassSession>({
    queryKey: () => scheduleQueryKeys.sessionById(sessionId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const id = sessionId();
      return db && id ? getSessionByIdQuery(db, id) : null;
    },
    enabled: () => Boolean(sessionId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}

/** A cohort by id (for name/course). */
export function useCohort(cohortId: Accessor<string | undefined>) {
  const base = useBase();
  return createPowerSyncQuery<Cohort>({
    queryKey: () => cohortQueryKeys.byId(cohortId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const id = cohortId();
      return db && id ? getCohortQuery(db, id) : null;
    },
    enabled: () => Boolean(cohortId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}

// D3 (plan 06 · E3): the `useCohortRoster` that used to live here was a
// literal COPY of the one in `features/academy/hooks/use-cohort-data` —same
// query, same cache key— and the live class screen now imports it from
// there. One query, one hook: two hooks over the same key drift out of sync
// as soon as one of the two changes source, which is exactly what this stage does.
